import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LocalizationProvider, DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AddRounded as AddIcon,
  MoreVertRounded as MoreVertIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
  SendRounded as SendIcon,
  VisibilityRounded as VisibilityIcon,
  CheckCircleRounded as CompletedIcon,
  CancelRounded as CancelIcon,
  TodayRounded as TodayIcon,
  ScheduleRounded as ScheduleIcon
} from '@mui/icons-material';
import { format, isValid, isSameDay, parseISO } from 'date-fns';
import appointmentService from '../../services/appointmentService';
import PageHeader from '../../components/ui/PageHeader';
import SearchField from '../../components/ui/SearchField';
import StatusChip from '../../components/ui/StatusChip';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';

const AppointmentsList = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  
  // Status change state
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [statusChangeId, setStatusChangeId] = useState(null);
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const data = await appointmentService.getAppointments();
        setAppointments(data);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Failed to load appointments. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointments();
  }, []);
  
  // Track the highlighted days (days with appointments)
  const [highlightedDays, setHighlightedDays] = useState({});
  const [calendarIsLoading, setCalendarIsLoading] = useState(false);
  
  // Process appointments to identify days with different types of appointments
  useEffect(() => {
    // Map to store date information
    const dateMap = {};
    
    appointments.forEach(appointment => {
      if (appointment.date) {
        const appointmentDate = typeof appointment.date === 'string'
          ? parseISO(appointment.date)
          : new Date(appointment.date);
        
        if (isValid(appointmentDate)) {
          const dateKey = format(appointmentDate, 'yyyy-MM-dd');
          const day = appointmentDate.getDate(); // Get day of month (1-31)
          
          // Initialize if needed
          if (!dateMap[dateKey]) {
            dateMap[dateKey] = {
              day,
              total: 0,
              scheduled: 0,
              completed: 0,
              canceled: 0,
              rescheduled: 0
            };
          }
          
          // Increment counters
          dateMap[dateKey].total += 1;
          
          // Increment specific status counter
          const status = appointment.status || 'scheduled';
          dateMap[dateKey][status] += 1;
        }
      }
    });
    
    // Convert to the format needed by the calendar
    setHighlightedDays(dateMap);
    setCalendarIsLoading(false);
  }, [appointments]);
  
  // Filter appointments based on date and search term
  useEffect(() => {
    let filtered = appointments;
    
    // Filter by selected date if any
    if (selectedDate) {
      filtered = filtered.filter(appointment => {
        const appointmentDate = appointment.date && typeof appointment.date === 'string' 
          ? parseISO(appointment.date) 
          : new Date(appointment.date);
        return isSameDay(appointmentDate, selectedDate);
      });
    }
    
    // Filter by search term if any
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(appointment => 
        appointment.customer?.name?.toLowerCase().includes(lowerCaseSearch) ||
        appointment.customer?.email?.toLowerCase().includes(lowerCaseSearch) ||
        appointment.notes?.toLowerCase().includes(lowerCaseSearch) ||
        appointment.timeSlot?.toLowerCase().includes(lowerCaseSearch)
      );
    }
    
    setFilteredAppointments(filtered);
  }, [searchTerm, selectedDate, appointments]);
  
  // Handle menu open
  const handleMenuOpen = (event, appointment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle delete appointment dialog open
  const handleOpenDeleteDialog = (id) => {
    const appointmentToDelete = appointments.find(appointment => appointment._id === id);
    setAppointmentToDelete(appointmentToDelete);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle delete appointment
  const handleDeleteAppointment = (id) => {
    handleOpenDeleteDialog(id);
  };
  
  // Handle confirm delete appointment
  const handleConfirmDelete = async () => {
    if (!appointmentToDelete) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setSuccessMessage(null);
      
      await appointmentService.deleteAppointment(appointmentToDelete._id);
      
      // Update state to remove the deleted appointment
      setAppointments(prevAppointments => 
        prevAppointments.filter(appointment => appointment._id !== appointmentToDelete._id)
      );
      
      // Close dialog
      setDeleteDialogOpen(false);
      setAppointmentToDelete(null);
      
      // Show success message
      setSuccessMessage('Appointment deleted successfully');
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setDeleteError(err.message || 'Failed to delete appointment. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Handle close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
    setDeleteError(null);
  };
  
  // Handle appointment status change
  const handleStatusChange = async (id, newStatus) => {
    if (!id) return;
    
    try {
      setStatusChangeLoading(true);
      setStatusChangeId(id);
      setError(null);
      setSuccessMessage(null);
      
      console.log(`Updating appointment ${id} status to ${newStatus}`);
      
      // Use the simpler status-only update approach first
      try {
        // This will use our special status update endpoint
        const updatedAppointment = await appointmentService.updateAppointment(id, { status: newStatus });
        console.log('Appointment status updated successfully:', updatedAppointment);
      } catch (statusUpdateError) {
        // If the simple approach fails, fall back to the full update approach
        console.warn('Simple status update failed, falling back to full update:', statusUpdateError);
        
        // Get the original appointment from our list to preserve required fields
        const originalAppointment = appointments.find(appointment => appointment._id === id);
        if (!originalAppointment) {
          throw new Error('Appointment not found');
        }
        
        // First, fetch full appointment details from server to ensure we have complete data
        const fullAppointment = await appointmentService.getAppointment(id);
        console.log('Retrieved full appointment data:', fullAppointment);
        
        // Use the server data to create the update payload
        const updateData = {
          status: newStatus,
          date: fullAppointment.date, 
          timeSlot: fullAppointment.timeSlot,
          customerId: fullAppointment.customer?._id || fullAppointment.customer
        };
        
        // Update the appointment with the full data
        const updatedAppointment = await appointmentService.updateAppointment(id, updateData);
        console.log('Updated appointment received (full update):', updatedAppointment);
      }
      
      // Update appointment list in the UI with the new status
      setAppointments(prevAppointments => 
        prevAppointments.map(appointment => 
          appointment._id === id 
            ? { ...appointment, status: newStatus } 
            : appointment
        )
      );
      
      // Show success message
      setSuccessMessage(`Appointment status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // Close the menu
      handleMenuClose();
    } catch (err) {
      console.error(`Error updating appointment status to ${newStatus}:`, err);
      // Show detailed error message to user
      let errorMessage = `Failed to update status to ${newStatus}.`;
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage += ` ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage += ` ${err.message}`;
      }
      setError(errorMessage);
      // Keep the menu open in case of error
    } finally {
      setStatusChangeLoading(false);
      setStatusChangeId(null);
    }
  };
  
  // Handle send to CRM
  const handleSendToCrm = async (id) => {
    if (!id) return;
    
    try {
      setStatusChangeLoading(true);
      setStatusChangeId(id);
      setError(null);
      setSuccessMessage(null);
      
      console.log(`Sending appointment ${id} to CRM`);
      
      // Send to CRM
      await appointmentService.sendToCrm(id);
      
      // Show success message
      setSuccessMessage('Appointment sent to CRM successfully');
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // Close the menu
      handleMenuClose();
    } catch (err) {
      console.error('Error sending appointment to CRM:', err);
      // Show detailed error message to user
      let errorMessage = 'Failed to send appointment to CRM.';
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage += ` ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage += ` ${err.message}`;
      }
      setError(errorMessage);
      // Keep the menu open in case of error
    } finally {
      setStatusChangeLoading(false);
      setStatusChangeId(null);
    }
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'canceled':
        return 'error';
      case 'rescheduled':
        return 'warning';
      case 'scheduled':
      default:
        return 'primary';
    }
  };
  
  // Custom Day renderer for the calendar
  const ServerDay = (props) => {
    const { highlightedDays, day, outsideCurrentMonth, ...other } = props;
    
    // Format date to match our map keys
    const dateKey = format(day, 'yyyy-MM-dd');
    const appointmentsForDay = highlightedDays[dateKey];
    
    // Skip if outside current month or no appointments
    if (outsideCurrentMonth || !appointmentsForDay) {
      return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />;
    }

    // Determine which badge to use based on appointment types
    let badgeContent = null;
    let badgeColor = 'primary';
    
    if (appointmentsForDay.canceled > 0 && appointmentsForDay.completed > 0 && appointmentsForDay.scheduled > 0) {
      // Mixed status - show the total count
      badgeContent = appointmentsForDay.total;
      badgeColor = 'secondary'; // Purple for mixed status
    } else if (appointmentsForDay.scheduled > 0) {
      // Scheduled appointments
      badgeContent = appointmentsForDay.scheduled;
      badgeColor = 'error'; // Red for scheduled
    } else if (appointmentsForDay.completed > 0) {
      // Completed appointments
      badgeContent = appointmentsForDay.completed;
      badgeColor = 'success'; // Green for completed
    } else if (appointmentsForDay.canceled > 0) {
      // Canceled appointments
      badgeContent = appointmentsForDay.canceled;
      badgeColor = 'primary'; // Blue for canceled
    }
    
    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        color={badgeColor}
        badgeContent={badgeContent > 0 ? badgeContent : undefined}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.65rem',
            height: 16,
            minWidth: 16,
            padding: '0 4px',
            right: 4,
            top: 3
          }
        }}
      >
        <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
      </Badge>
    );
  };
  
  if (loading) {
    return <LoadingState label="Loading appointments…" />;
  }

  return (
    <Box>
      <PageHeader
        title="Appointments"
        subtitle="Pick a date to see that day's schedule"
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/appointments/new')}
          >
            New Appointment
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        {/* Calendar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: { md: 'sticky' }, top: { md: 84 } }}>
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={(newDate) => {
                    // Prevent unnecessary re-renders by checking if date actually changed
                    if (!isSameDay(newDate, selectedDate)) {
                      setSelectedDate(newDate);
                    }
                  }}
                  loading={calendarIsLoading}
                  renderLoading={() => <DayCalendarSkeleton />}
                  onMonthChange={() => {
                    // Optional: You can load data for the new month here if needed
                    // setCalendarIsLoading(true);
                    // loadAppointmentsForMonth(month);
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: 'primary.main',
                    },
                  }}
                  slots={{
                    day: ServerDay,
                  }}
                  slotProps={{
                    day: {
                      highlightedDays,
                    },
                  }}
                />
              </LocalizationProvider>
              
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  columnGap: 2,
                  rowGap: 0.75,
                }}
              >
                {[
                  { color: 'error.main', label: 'Scheduled' },
                  { color: 'success.main', label: 'Completed' },
                  { color: 'primary.main', label: 'Canceled' },
                  { color: 'secondary.main', label: 'Mixed' },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      aria-hidden
                      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Appointments list */}
        <Grid item xs={12} md={8}>
          <SearchField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search appointments…"
            sx={{ mb: 2 }}
          />

          {/* Date header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h5">
              {selectedDate ? format(selectedDate, 'EEEE, MMMM dd') : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredAppointments.length} appointment{filteredAppointments.length === 1 ? '' : 's'}
            </Typography>
          </Stack>

          {/* Appointments */}
          {filteredAppointments.length > 0 ? (
            <Stack spacing={1.5}>
              {filteredAppointments.map((appointment) => (
                <Card key={appointment._id}>
                  <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                    <Stack direction="row" spacing={1.75} alignItems="flex-start">
                      <Box
                        aria-hidden
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: '13px',
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: (t) =>
                            alpha(
                              t.palette[getStatusColor(appointment.status)]?.main || t.palette.primary.main,
                              0.12
                            ),
                          color: `${getStatusColor(appointment.status)}.main`,
                        }}
                      >
                        <ScheduleIcon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="subtitle1" noWrap>
                            {appointment.timeSlot || 'Time TBD'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <StatusChip status={appointment.status || 'scheduled'} />
                            <IconButton
                              size="small"
                              aria-label="Appointment actions"
                              onClick={(e) => handleMenuOpen(e, appointment)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }} noWrap>
                          {appointment.customer?.name || 'Unknown Customer'}
                        </Typography>
                        {(appointment.customer?.phone || appointment.customer?.email) && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {appointment.customer?.phone || appointment.customer?.email}
                          </Typography>
                        )}
                        {appointment.notes && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.75,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {appointment.notes}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Card>
              <EmptyState
                icon={<TodayIcon />}
                title="No appointments"
                description={
                  searchTerm
                    ? 'No appointments match your search criteria.'
                    : selectedDate
                    ? `Nothing scheduled for ${format(selectedDate, 'MMMM dd, yyyy')}.`
                    : 'No appointments scheduled yet.'
                }
                color="info"
                action={
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/appointments/new')}
                  >
                    Schedule Appointment
                  </Button>
                }
              />
            </Card>
          )}
        </Grid>
      </Grid>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
      >
        <MenuItem onClick={() => navigate(`/appointments/${selectedAppointment?._id}`)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => navigate(`/appointments/${selectedAppointment?._id}/edit`)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleStatusChange(selectedAppointment?._id, 'completed')}
          disabled={selectedAppointment?.status === 'completed' || (statusChangeLoading && statusChangeId === selectedAppointment?._id)}
        >
          <ListItemIcon>
            {statusChangeLoading && statusChangeId === selectedAppointment?._id ? (
              <CircularProgress size={20} color="success" />
            ) : (
              <CompletedIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          Mark as Completed
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange(selectedAppointment?._id, 'canceled')}
          disabled={selectedAppointment?.status === 'canceled' || (statusChangeLoading && statusChangeId === selectedAppointment?._id)}
        >
          <ListItemIcon>
            {statusChangeLoading && statusChangeId === selectedAppointment?._id ? (
              <CircularProgress size={20} color="error" />
            ) : (
              <CancelIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          Cancel Appointment
        </MenuItem>
        <MenuItem 
          onClick={() => navigate(`/appointments/${selectedAppointment?._id}/reschedule`)}
          disabled={statusChangeLoading && statusChangeId === selectedAppointment?._id}
        >
          <ListItemIcon>
            <ScheduleIcon fontSize="small" color="warning" />
          </ListItemIcon>
          Reschedule
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleSendToCrm(selectedAppointment?._id)}
          disabled={statusChangeLoading && statusChangeId === selectedAppointment?._id}
        >
          <ListItemIcon>
            {statusChangeLoading && statusChangeId === selectedAppointment?._id ? (
              <CircularProgress size={20} />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </ListItemIcon>
          Send to CRM
        </MenuItem>
        <MenuItem onClick={() => handleDeleteAppointment(selectedAppointment?._id)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete appointment?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently remove the appointment and cannot be undone.
          </Typography>
          {appointmentToDelete && (
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                border: (t) => `1px solid ${alpha(t.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="subtitle2">
                {appointmentToDelete.customer?.name || 'Unknown customer'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {appointmentToDelete.date
                  ? format(new Date(appointmentToDelete.date), 'MMMM dd, yyyy')
                  : 'Unknown date'}
                {' · '}
                {appointmentToDelete.timeSlot || 'Unknown time'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ backgroundImage: 'none' }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentsList;